import { DISTANCE_UNITS, ID_IMO, MODE_WATER } from '@/lib/constants';
import { ShipmentCargo } from '@/lib/models/shipment/types';
import { VehicleModel } from '@/lib/models/vehicle/types';
import { SegmentModeType } from '@/types/api-types';
import { convertSegmentModeVehicleType } from '@/utils/format/mode';
import { formatWeight } from '@/utils/format/weight';
import { getPreferredIdentifier } from '@/utils/helpers';
import { roundNumberToFixed } from '@/utils/helpers/maths';
import { isContainerIsoReefer } from '@/utils/shipments';
import { tv } from '@/utils/styles';

export interface EmissionsAssumptionsProps {
  mode: SegmentModeType;
  vehicle?: VehicleModel;
  distance?: number;
  cargo: ShipmentCargo[];
}

const EmissionsAssumptions = ({ mode, vehicle, distance, cargo = [] }: EmissionsAssumptionsProps) => {
  const vehicleType = convertSegmentModeVehicleType(mode);
  const vehicleIdentifier = vehicle?.identifiers ? getPreferredIdentifier(vehicle?.identifiers) : null;

  // Vessels
  const isWaterVehicle = mode === MODE_WATER;
  const hasImo = vehicleIdentifier?.type === ID_IMO;

  // Cargo
  const weightUnit = cargo[0].weight?.unit;
  const totalWeight = cargo.reduce((accum, item) => accum + (item.weight?.value || 0), 0);
  const isReefer = cargo.length === 1 ? isContainerIsoReefer(cargo[0]?.container?.iso) : false;

  const assumptions = [
    {
      id: 'vehicleType',
      label: 'Vehicle type',
      isVisible: !!vehicleType,
      value: vehicleType ? vehicleType.toUpperCase() : '',
      defaultValue: 'null',
      reason: 'inferred from transit characteristics',
      defaultReason: 'no value provided',
    },
    {
      id: 'emissionsFactorWater',
      label: 'Emissions factor',
      isVisible: isWaterVehicle,
      value: hasImo ? 'Vehicle factor' : '',
      defaultValue: 'Trade-lane avg.',
      reason: `inferred from supplied vehicle: IMO ${vehicleIdentifier?.value}`,
      defaultReason: 'no specific vehicle provided',
    },
    {
      id: 'refrigerated',
      label: 'Refrigerated',
      isVisible: true,
      value: isReefer ? 'True' : 'False',
      defaultValue: 'False',
      reason: 'inferred from cargo description',
      defaultReason: 'no value provided',
    },
    {
      id: 'weight',
      label: 'Weight',
      isVisible: true,
      value: weightUnit ? formatWeight({ value: totalWeight, unit: weightUnit }) : '',
      defaultValue: 'null', // @TODO get weight defaults
      reason: 'inferred from cargo description',
      defaultReason: 'no value provided, set to methodology default.',
    },
    {
      id: 'distance',
      label: 'Distance',
      isVisible: true,
      value: '', // @TODO determine how we communicate actual distance
      defaultValue: distance ? `${roundNumberToFixed(distance)} ${DISTANCE_UNITS.KM}` : 'N/A',
      reason: 'input value provided',
      defaultReason: 'exact distance unknown, modelled distance used.',
    },
  ];

  const { dl, highlightValue, highlightReason } = styles();

  return (
    <div>
      {assumptions.map((row) =>
        row.isVisible ? (
          <dl key={row.id} className={dl()}>
            <dt>{row.label}</dt>
            <dd>
              We assumed <span className={highlightValue()}>{row.value || row.defaultValue}</span> because{' '}
              <span className={highlightReason()}>{row.value ? row.reason : row.defaultReason}</span>
            </dd>
          </dl>
        ) : null,
      )}
    </div>
  );
};

const styles = tv({
  slots: {
    dl: 'mb-2 grid grid-cols-[150px_1fr] text-baseSm [&_dt]:font-medium',
    highlightValue: 'bg-green-100/50 px-1 py-0.5 font-mono text-[85%] uppercase text-green-800',
    highlightReason: 'bg-lightBlue-50 px-1 py-0.5 font-mono text-[85%] text-lightBlue-900',
  },
});

export { EmissionsAssumptions };
